import {ChangeEventHandler, useEffect, useState, useRef, useCallback} from "react";
import Title from "../Header/Title";
import Utils from "Services/Utils";
import { useAppContext } from "contexts/AppContext";
import { BUILDER_CONFIG, CONFIG_CUSTOMIZATION, CUSTOMIZATION_THEME_SETTINGS, PAGE_LAYOUT, PAGE_LAYOUTS, THEME_PLATFORMS, THEME_SECONDARY } from "constants/constants";
import { TInfoText } from "types";
import { debounce } from "lodash";

type MyProps = {
    id: string
    name: string
    value?: string[]|number[]
    onChange: (_value?: number) => void
    placeholder?: string
    disabled?: boolean
    required?: boolean
    error?: string
    min?: number|string,
    max?: number|string,
    increment?: number|string
    description?: string
    info?: TInfoText
    showLabel?: boolean
    isFilter?: boolean
}

const NumberField = (props: MyProps) => {
    const {platform, builderSpec} = useAppContext()
    const [_value, setValue] = useState("")
    const [error, setError] = useState<string|null>(null)

    useEffect(() => {
        setValue(String(props.value || ""))
    }, [props.value])

    useEffect(() => {
        if(_value === "") return setError(null)
        const _error = Utils.validateNumericField(_value, {min: props.min, max: props.max, increment: props.increment})
        setError(_error)
    }, [_value])

    const submit = (value: string) => {
        const _error = Utils.validateNumericField(value, {min: props.min, max: props.max, increment: props.increment})
        setError(_error)
        if(!_error && Number(props.value) !== Number(value)) props.onChange((value.trim() !== '') ? Number(value) : undefined)
    }

    // Create debounced submit function
    const debouncedSubmit = useCallback(
        debounce((value) => {
            submit(value)
        }, 500),
        [] // Dependencies that should recreate the debounced function
    )

    const handleBlur: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        debouncedSubmit.cancel() // Cancel any pending debounced submissions
        submit(_value) // Submit immediately on blur
    }

    const handleKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if(e.key === 'Enter'){
            e.preventDefault()
            debouncedSubmit.cancel() // Cancel any pending debounced submissions
            submit(_value) // Submit immediately on Enter
        }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value)
        // Only trigger debounced submit for input events
        if (e.type === 'input') {
            debouncedSubmit(e.target.value)
        }
    }
    
    // Clean up on unmount
    useEffect(() => {
        return () => {
            debouncedSubmit.cancel()
        }
    }, [debouncedSubmit])

    return ( 
        <div className="sb3-space-y-2">
            <Title title={props.name} required={props.required} description={props.description} info={props.info} showLabel={props.showLabel} id={props.id}/>
            <div className="sb3-space-y-1">
                <input 
                    disabled={props.disabled}  
                    min={props.min === undefined ? 0 : props.min} 
                    max={props.max ?? undefined}  
                    style={{ 
                        backgroundColor: platform === THEME_PLATFORMS.BIG_COMMERCE && !props.disabled 
                            ? builderSpec?.[BUILDER_CONFIG]?.[CONFIG_CUSTOMIZATION]?.[CUSTOMIZATION_THEME_SETTINGS]?.[THEME_SECONDARY] 
                            : undefined
                    }}
                    onWheel={(e) => (e.target as any).blur()} 
                    onBlur={handleBlur} 
                    onKeyDown={handleKeyPress} 
                    onChange={handleChange} 
                    onInput={handleChange} 
                    value={_value} 
                    type="number" 
                    id={"number-input" + props.id}
                    placeholder={props.placeholder && ''}
                    className={`disabled:sb3-bg-disabled ${(platform === THEME_PLATFORMS.BIG_COMMERCE) ? 'sb3-rounded-lg':'sb3-rounded-none' } 
                    sb3-bg-white sb3-border sb3-border-solid sb3-border-gray-300 sb3-text-gray-900 sb3-text-sm 
                    focus:sb3-ring-0 focus:sb3-outline-none sb3-block ${!props.isFilter ? '!sb3-w-60' : 'sb3-w-full'} 
                    sb3-max-w-full sb3-p-2.5`}
                />

                {error &&
                    <p id="helper-text-explanation" className="sb3-text-sm sb3-text-red-500">{error}</p>}
            </div>
        </div>
    )
};

export default NumberField